import {
    closestCenter,
    DndContext,
    DragEndEvent,
    KeyboardSensor,
    PointerSensor,
    useSensor,
    useSensors,
} from '@dnd-kit/core';
import {
    arrayMove,
    SortableContext,
    sortableKeyboardCoordinates,
    useSortable,
    verticalListSortingStrategy,
} from '@dnd-kit/sortable';
import { CSS } from '@dnd-kit/utilities';
import {
    Calendar,
    Edit2,
    FileText,
    GripVertical,
    Hash,
    List,
    Plus,
    Save,
    Settings,
    Trash2,
    Type
} from 'lucide-react';
import React, { useEffect, useState } from 'react';
import type { ColumnMetadata, FieldType, FieldWithMetadata } from '../../types/columnMetadata';
import { Button } from '../ui/button';
import {
    Dialog,
    DialogContent,
    DialogDescription,
    DialogFooter,
    DialogHeader,
    DialogTitle,
} from '../ui/dialog';
import { Input } from '../ui/input';
import { Label } from '../ui/label';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../ui/tabs';
import { Textarea } from '../ui/textarea';
import { ColumnMetadataEditor } from './ColumnMetadataEditor';

type Field = FieldWithMetadata;

type TableFieldsEditorProps = {
  value: string;
  onChange: (jsonString: string) => void;
};

const FIELD_TYPES: { value: FieldType; label: string; icon: React.ReactNode }[] = [
  { value: 'singleLineText', label: 'Single Line Text', icon: <Type className="w-4 h-4" /> },
  { value: 'longText', label: 'Long Text', icon: <FileText className="w-4 h-4" /> },
  { value: 'number', label: 'Number', icon: <Hash className="w-4 h-4" /> },
  { value: 'date', label: 'Date', icon: <Calendar className="w-4 h-4" /> },
  { value: 'singleSelect', label: 'Single Select', icon: <List className="w-4 h-4" /> },
  { value: 'multipleSelects', label: 'Multiple Select', icon: <List className="w-4 h-4" /> },
  { value: 'checkbox', label: 'Checkbox', icon: <Type className="w-4 h-4" /> },
  { value: 'email', label: 'Email', icon: <Type className="w-4 h-4" /> },
  { value: 'url', label: 'URL', icon: <Type className="w-4 h-4" /> },
  { value: 'phoneNumber', label: 'Phone Number', icon: <Type className="w-4 h-4" /> },
];

const SortableFieldItem: React.FC<{
  field: Field;
  index: number;
  onEdit: (index: number) => void;
  onDelete: (index: number) => void;
}> = ({ field, index, onEdit, onDelete }) => {
  const {
    attributes,
    listeners,
    setNodeRef,
    transform,
    transition,
    isDragging,
  } = useSortable({ id: `field-${index}` });

  const style = {
    transform: CSS.Transform.toString(transform),
    transition,
    opacity: isDragging ? 0.5 : 1,
  };

  const fieldTypeInfo = FIELD_TYPES.find((ft) => ft.value === field.type);
  const hasMetadata = !!(
    field.metadata?.validation ||
    field.metadata?.formatting ||
    field.metadata?.display ||
    field.metadata?.conditions ||
    field.metadata?.ui
  );

  return (
    <div
      ref={setNodeRef}
      style={style}
      className={`flex items-center gap-3 p-3 border rounded-lg bg-card hover:bg-muted/50 transition-colors ${
        isDragging ? 'shadow-lg' : ''
      }`}
    >
      <div
        {...attributes}
        {...listeners}
        className="cursor-grab active:cursor-grabbing text-muted-foreground hover:text-foreground"
      >
        <GripVertical className="w-5 h-5" />
      </div>
      <div className="flex-1 min-w-0">
        <div className="flex items-center gap-2">
          {fieldTypeInfo?.icon}
          <span className="font-medium truncate">{field.name}</span>
          <span className="text-xs text-muted-foreground truncate">
            ({field.dbFieldName})
          </span>
          {hasMetadata && (
            <span className="text-xs text-primary" title="Has metadata">
              <Settings className="w-3 h-3" />
            </span>
          )}
        </div>
        {field.description && (
          <p className="text-xs text-muted-foreground truncate mt-1">
            {field.description}
          </p>
        )}
      </div>
      <div className="flex items-center gap-1">
        <Button
          variant="ghost"
          size="sm"
          onClick={() => onEdit(index)}
          className="h-8 w-8 p-0"
        >
          <Edit2 className="w-4 h-4" />
        </Button>
        <Button
          variant="ghost"
          size="sm"
          onClick={() => onDelete(index)}
          className="h-8 w-8 p-0 text-destructive hover:text-destructive"
        >
          <Trash2 className="w-4 h-4" />
        </Button>
      </div>
    </div>
  );
};

export const TableFieldsEditor: React.FC<TableFieldsEditorProps> = ({
  value,
  onChange,
}) => {
  const [fields, setFields] = useState<Field[]>([]);
  const [editingIndex, setEditingIndex] = useState<number | null>(null);
  const [isAddDialogOpen, setIsAddDialogOpen] = useState(false);
  const [formData, setFormData] = useState<Field>({
    type: 'singleLineText',
    name: '',
    dbFieldName: '',
    description: '',
    options: {},
    metadata: undefined,
  });

  const sensors = useSensors(
    useSensor(PointerSensor),
    useSensor(KeyboardSensor, {
      coordinateGetter: sortableKeyboardCoordinates,
    })
  );

  // Check if metadata has any content worth saving
  const hasMetadataContent = (metadata?: ColumnMetadata): boolean => {
    if (!metadata) return false;
    return !!(
      (metadata.validation && Object.keys(metadata.validation).length > 0) ||
      (metadata.formatting && Object.keys(metadata.formatting).length > 0) ||
      (metadata.display && Object.keys(metadata.display).length > 0) ||
      (metadata.conditions && Object.keys(metadata.conditions).length > 0) ||
      (metadata.fieldSpecific && Object.keys(metadata.fieldSpecific).length > 0) ||
      (metadata.ui && Object.keys(metadata.ui).length > 0)
    );
  };

  // Clean field for serialization - only include properties with values
  const cleanFieldForSerialization = (field: Field): Field => {
    const cleaned: Field = {
      type: field.type,
      name: field.name,
      dbFieldName: field.dbFieldName,
    };

    if (field.description) cleaned.description = field.description;
    if (field.options && Object.keys(field.options).length > 0) {
      cleaned.options = field.options;
    }
    if (hasMetadataContent(field.metadata)) {
      cleaned.metadata = field.metadata;
    }

    return cleaned;
  };

  useEffect(() => {
    try {
      const parsed = JSON.parse(value || '{}');
      // Preserve metadata when loading fields from JSON
      const loadedFields = (parsed.fields || []).map((field: Field) => ({
        ...field,
        // Ensure metadata is preserved if it exists
        metadata: field.metadata || undefined,
        // Ensure options is an object if it exists, otherwise undefined
        options: field.options && Object.keys(field.options).length > 0 ? field.options : undefined,
      }));
      setFields(loadedFields);
    } catch {
      setFields([]);
    }
  }, [value]);

  const updateJsonString = (newFields: Field[]) => {
    try {
      const parsed = JSON.parse(value || '{}');
      // Clean all fields before serialization
      const cleanedFields = newFields.map(cleanFieldForSerialization);
      const updated = {
        ...parsed,
        fields: cleanedFields,
      };
      onChange(JSON.stringify(updated, null, 2));
    } catch {
      // Clean all fields before serialization
      const cleanedFields = newFields.map(cleanFieldForSerialization);
      const updated = {
        fields: cleanedFields,
      };
      onChange(JSON.stringify(updated, null, 2));
    }
  };

  const handleDragEnd = (event: DragEndEvent) => {
    const { active, over } = event;

    if (over && active.id !== over.id) {
      const oldIndex = parseInt(active.id.toString().replace('field-', ''));
      const newIndex = parseInt(over.id.toString().replace('field-', ''));

      const newFields = arrayMove(fields, oldIndex, newIndex);
      setFields(newFields);
      updateJsonString(newFields);
    }
  };

  const handleAdd = () => {
    const newField: Field = {
      type: formData.type,
      name: formData.name,
      dbFieldName: formData.dbFieldName || formData.name.toLowerCase().replace(/\s+/g, '_'),
      description: formData.description || undefined,
      options: formData.options && Object.keys(formData.options).length > 0 ? formData.options : undefined,
      // Only include metadata if it has content
      ...(hasMetadataContent(formData.metadata) ? { metadata: formData.metadata } : {}),
    };

    const newFields = [...fields, newField];
    setFields(newFields);
    updateJsonString(newFields);
    setIsAddDialogOpen(false);
    setFormData({
      type: 'singleLineText',
      name: '',
      dbFieldName: '',
      description: '',
      options: {},
      metadata: undefined,
    });
  };

  const handleEdit = (index: number) => {
    setEditingIndex(index);
    setFormData(fields[index]);
  };

  const handleSaveEdit = () => {
    if (editingIndex === null) return;

    const updatedFields = [...fields];
    updatedFields[editingIndex] = {
      type: formData.type,
      name: formData.name,
      dbFieldName: formData.dbFieldName || formData.name.toLowerCase().replace(/\s+/g, '_'),
      description: formData.description || undefined,
      options: formData.options && Object.keys(formData.options).length > 0 ? formData.options : undefined,
      // Only include metadata if it has content
      ...(hasMetadataContent(formData.metadata) ? { metadata: formData.metadata } : {}),
    };
    setFields(updatedFields);
    updateJsonString(updatedFields);
    setEditingIndex(null);
    setFormData({
      type: 'singleLineText',
      name: '',
      dbFieldName: '',
      description: '',
      options: {},
      metadata: undefined,
    });
  };

  const handleDelete = (index: number) => {
    const newFields = fields.filter((_, i) => i !== index);
    setFields(newFields);
    updateJsonString(newFields);
  };

  const generateDbFieldName = (name: string) => {
    return name.toLowerCase().replace(/\s+/g, '_').replace(/[^a-z0-9_]/g, '');
  };

  return (
    <div className="space-y-4">
      <div className="flex items-center justify-between">
        <Label>Fields ({fields.length})</Label>
        <Button
          type="button"
          variant="outline"
          size="sm"
          onClick={() => setIsAddDialogOpen(true)}
        >
          <Plus className="w-4 h-4 mr-2" />
          Add Field
        </Button>
      </div>

      <DndContext
        sensors={sensors}
        collisionDetection={closestCenter}
        onDragEnd={handleDragEnd}
      >
        <SortableContext
          items={fields.map((_, index) => `field-${index}`)}
          strategy={verticalListSortingStrategy}
        >
          <div className="space-y-2">
            {fields.length === 0 ? (
              <div className="text-center py-8 text-muted-foreground border rounded-lg">
                No fields added. Click "Add Field" to get started.
              </div>
            ) : (
              fields.map((field, index) => (
                <SortableFieldItem
                  key={`field-${index}`}
                  field={field}
                  index={index}
                  onEdit={handleEdit}
                  onDelete={handleDelete}
                />
              ))
            )}
          </div>
        </SortableContext>
      </DndContext>

      {/* Add Field Dialog */}
      <Dialog open={isAddDialogOpen} onOpenChange={setIsAddDialogOpen}>
        <DialogContent className="max-w-4xl max-h-[90vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle>Add Field</DialogTitle>
            <DialogDescription>
              Configure a new field for your table
            </DialogDescription>
          </DialogHeader>
          <Tabs defaultValue="basic" className="w-full">
            <TabsList>
              <TabsTrigger value="basic">Basic</TabsTrigger>
              <TabsTrigger value="metadata">Metadata</TabsTrigger>
            </TabsList>
            <TabsContent value="basic" className="space-y-4 py-4">
              <div>
                <Label htmlFor="field-type">Field Type *</Label>
                <div className="grid grid-cols-2 gap-2 mt-2">
                  {FIELD_TYPES.map((ft) => (
                    <Button
                      key={ft.value}
                      type="button"
                      variant={formData.type === ft.value ? 'default' : 'outline'}
                      onClick={() => setFormData({ ...formData, type: ft.value as FieldType })}
                      className="justify-start h-auto py-3"
                    >
                      <div className="flex items-center gap-2 w-full">
                        {ft.icon}
                        <span className="text-sm">{ft.label}</span>
                      </div>
                    </Button>
                  ))}
                </div>
              </div>
              <div>
                <Label htmlFor="field-name">Field Name *</Label>
                <Input
                  id="field-name"
                  value={formData.name}
                  onChange={(e) => {
                    const name = e.target.value;
                    setFormData({
                      ...formData,
                      name,
                      dbFieldName: formData.dbFieldName || generateDbFieldName(name),
                    });
                  }}
                  placeholder="e.g., Contact Name"
                />
              </div>
              <div>
                <Label htmlFor="field-db-name">Database Field Name *</Label>
                <Input
                  id="field-db-name"
                  value={formData.dbFieldName}
                  onChange={(e) =>
                    setFormData({ ...formData, dbFieldName: e.target.value })
                  }
                  placeholder="e.g., contact_name"
                />
                <p className="text-xs text-muted-foreground mt-1">
                  Used in database queries (snake_case recommended)
                </p>
              </div>
              <div>
                <Label htmlFor="field-description">Description</Label>
                <Textarea
                  id="field-description"
                  value={formData.description || ''}
                  onChange={(e) =>
                    setFormData({ ...formData, description: e.target.value })
                  }
                  placeholder="Optional description for this field"
                  rows={2}
                />
              </div>
            </TabsContent>
            <TabsContent value="metadata" className="py-4">
              <ColumnMetadataEditor
                fieldType={formData.type}
                metadata={formData.metadata}
                onChange={(metadata) => setFormData({ ...formData, metadata })}
              />
            </TabsContent>
          </Tabs>
          <DialogFooter>
            <Button
              variant="outline"
              onClick={() => setIsAddDialogOpen(false)}
            >
              Cancel
            </Button>
            <Button
              onClick={handleAdd}
              disabled={!formData.name || !formData.dbFieldName}
            >
              Add Field
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Edit Field Dialog */}
      <Dialog open={editingIndex !== null} onOpenChange={(open) => !open && setEditingIndex(null)}>
        <DialogContent className="max-w-4xl max-h-[90vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle>Edit Field</DialogTitle>
            <DialogDescription>
              Update field configuration
            </DialogDescription>
          </DialogHeader>
          <Tabs defaultValue="basic" className="w-full">
            <TabsList>
              <TabsTrigger value="basic">Basic</TabsTrigger>
              <TabsTrigger value="metadata">Metadata</TabsTrigger>
            </TabsList>
            <TabsContent value="basic" className="space-y-4 py-4">
              <div>
                <Label htmlFor="edit-field-type">Field Type *</Label>
                <div className="grid grid-cols-2 gap-2 mt-2">
                  {FIELD_TYPES.map((ft) => (
                    <Button
                      key={ft.value}
                      type="button"
                      variant={formData.type === ft.value ? 'default' : 'outline'}
                      onClick={() => setFormData({ ...formData, type: ft.value as FieldType })}
                      className="justify-start h-auto py-3"
                    >
                      <div className="flex items-center gap-2 w-full">
                        {ft.icon}
                        <span className="text-sm">{ft.label}</span>
                      </div>
                    </Button>
                  ))}
                </div>
              </div>
              <div>
                <Label htmlFor="edit-field-name">Field Name *</Label>
                <Input
                  id="edit-field-name"
                  value={formData.name}
                  onChange={(e) => {
                    const name = e.target.value;
                    setFormData({
                      ...formData,
                      name,
                      dbFieldName: formData.dbFieldName || generateDbFieldName(name),
                    });
                  }}
                />
              </div>
              <div>
                <Label htmlFor="edit-field-db-name">Database Field Name *</Label>
                <Input
                  id="edit-field-db-name"
                  value={formData.dbFieldName}
                  onChange={(e) =>
                    setFormData({ ...formData, dbFieldName: e.target.value })
                  }
                />
              </div>
              <div>
                <Label htmlFor="edit-field-description">Description</Label>
                <Textarea
                  id="edit-field-description"
                  value={formData.description || ''}
                  onChange={(e) =>
                    setFormData({ ...formData, description: e.target.value })
                  }
                  rows={2}
                />
              </div>
            </TabsContent>
            <TabsContent value="metadata" className="py-4">
              <ColumnMetadataEditor
                fieldType={formData.type}
                metadata={formData.metadata}
                onChange={(metadata) => setFormData({ ...formData, metadata })}
              />
            </TabsContent>
          </Tabs>
          <DialogFooter>
            <Button
              variant="outline"
              onClick={() => setEditingIndex(null)}
            >
              Cancel
            </Button>
            <Button
              onClick={handleSaveEdit}
              disabled={!formData.name || !formData.dbFieldName}
            >
              <Save className="w-4 h-4 mr-2" />
              Save Changes
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
};

